import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, loading: true, error: null };
    case 'LOGIN_SUCCESS':
      return { 
        ...state, 
        loading: false, 
        user: action.payload.user, 
        token: action.payload.token,
        isAuthenticated: true,
        error: null
      };
    case 'LOGIN_FAILURE':
      return { 
        ...state, 
        loading: false, 
        error: action.payload, 
        isAuthenticated: false 
      };
    case 'LOGOUT':
      return { 
        ...state, 
        user: null, 
        token: null, 
        isAuthenticated: false, 
        loading: false 
      };
    case 'REFRESH_SUCCESS':
      return { 
        ...state, 
        loading: false,
        isAuthenticated: true,
        token: action.payload.token,
        user: action.payload.user 
      };
    case 'AUTH_INIT_COMPLETE':
      return { ...state, loading: false };
    case 'SET_IMPERSONATION':
      return { 
        ...state, 
        impersonatedBy: action.payload 
      };
    case 'CLEAR_IMPERSONATION':
      return { 
        ...state, 
        impersonatedBy: null 
      };
    default:
      return state;
  }
};

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true,
  error: null,
  impersonatedBy: null
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for stored token on mount
  useEffect(() => {
    const checkStoredAuth = async () => {
      // Check localStorage first, then sessionStorage
      let token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      let user = localStorage.getItem('user') || sessionStorage.getItem('user');
      let orgMemberships = localStorage.getItem('org_memberships') || sessionStorage.getItem('org_memberships');
      
      if (token && user) {
        try {
          const decodedToken = jwtDecode(token);
          const isExpired = decodedToken.exp * 1000 < Date.now();
          
          if (!isExpired) {
            const userData = JSON.parse(user);
            const memberships = orgMemberships ? JSON.parse(orgMemberships) : [];
            
            dispatch({ 
              type: 'LOGIN_SUCCESS', 
              payload: { 
                token, 
                user: { ...userData, org_memberships: memberships }
              } 
            });
            
            // Set axios default header
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            return; // LOGIN_SUCCESS sets loading=false
          } else {
            // Token expired, try to refresh (async — wait for it)
            await refreshTokenIfExists();
            // After refresh: either REFRESH_SUCCESS (loading=false) or LOGOUT (loading=false)
            // or no refresh token — fall through to AUTH_INIT_COMPLETE
          }
        } catch (error) {
          // Invalid token, clear storage — fall through to AUTH_INIT_COMPLETE
          clearStoredAuth();
        }
      }
      // No valid token / expired & no refresh / invalid token — signal init complete
      dispatch({ type: 'AUTH_INIT_COMPLETE' });
    };
    
    checkStoredAuth();
  }, []);

  const login = async (email, password, rememberMe = false) => {
    dispatch({ type: 'LOGIN_START' });
    
    try {
      const response = await axios.post(`${API}/auth/login`, { 
        email, 
        password, 
        remember: rememberMe 
      });
      
      const { 
        access_token, 
        refresh_token, 
        user, 
        org_memberships 
      } = response.data;
      
      // Store tokens and user data
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('access_token', access_token);
      storage.setItem('refresh_token', refresh_token);
      storage.setItem('user', JSON.stringify(user));
      storage.setItem('org_memberships', JSON.stringify(org_memberships));
      
      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      dispatch({ 
        type: 'LOGIN_SUCCESS', 
        payload: { 
          token: access_token, 
          user: { ...user, org_memberships }
        } 
      });
      
      return { success: true, user: { ...user, org_memberships } };
    } catch (error) {
      const message = error.response?.data?.message || 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.';
      dispatch({ type: 'LOGIN_FAILURE', payload: message });
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');
      
      if (refreshToken) {
        await axios.post(`${API}/auth/logout`, { 
          refresh_token: refreshToken 
        });
      }
      
      // Clear storage
      clearStoredAuth();
      
      // Clear axios header
      delete axios.defaults.headers.common['Authorization'];
      
      dispatch({ type: 'LOGOUT' });
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if API call fails
      clearStoredAuth();
      delete axios.defaults.headers.common['Authorization'];
      dispatch({ type: 'LOGOUT' });
    }
  };

  const refreshTokenIfExists = async () => {
    const refreshToken = localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        await refreshTokenFunc(refreshToken);
      } catch (error) {
        clearStoredAuth();
      }
    }
  };

  const refreshTokenFunc = async (refreshToken) => {
    try {
      const response = await axios.post(`${API}/auth/refresh`, { 
        refresh_token: refreshToken 
      });
      
      const { 
        access_token, 
        user, 
        org_memberships 
      } = response.data;
      
      // Update storage
      const storage = localStorage.getItem('refresh_token') ? localStorage : sessionStorage;
      storage.setItem('access_token', access_token);
      storage.setItem('user', JSON.stringify(user));
      storage.setItem('org_memberships', JSON.stringify(org_memberships));
      
      // Update axios header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      dispatch({ 
        type: 'REFRESH_SUCCESS', 
        payload: { 
          token: access_token, 
          user: { ...user, org_memberships }
        } 
      });
      
      return access_token;
    } catch (error) {
      // Refresh failed, force logout
      await logout();
      throw error;
    }
  };

  const clearStoredAuth = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('org_memberships');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('org_memberships');
  };

  const impersonateUser = async (userId) => {
    try {
      const response = await axios.post(`${API}/ultra/impersonate`, { user_id: userId });
      const { access_token, user, impersonated_by } = response.data;
      
      // Update token
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      dispatch({ 
        type: 'REFRESH_SUCCESS', 
        payload: { token: access_token, user } 
      });
      
      dispatch({ 
        type: 'SET_IMPERSONATION', 
        payload: impersonated_by 
      });
      
      await auditLog('IMPERSONATE_START', 'user', userId, { impersonated_by });
      
    } catch (error) {
      throw error;
    }
  };

  const stopImpersonation = async () => {
    try {
      await axios.post(`${API}/ultra/stop-impersonate`);
      
      // Refresh to get original token back
      await refreshToken();
      
      dispatch({ type: 'CLEAR_IMPERSONATION' });
      
      await auditLog('IMPERSONATE_END', 'user', null, {});
      
    } catch (error) {
      throw error;
    }
  };

  const auditLog = async (action, entity, entityId, metadata = {}) => {
    try {
      await axios.post(`${API}/audit/log`, {
        action,
        entity,
        entity_id: entityId,
        metadata
      });
    } catch (error) {
      console.error('Audit log error:', error);
    }
  };

  const hasRole = (requiredRoles) => {
    if (!state.user || !state.user.org_memberships) {
      return false;
    }
    
    const userRoles = state.user.org_memberships.map(membership => membership.role);
    
    if (Array.isArray(requiredRoles)) {
      return requiredRoles.some(role => userRoles.includes(role));
    }
    
    return userRoles.includes(requiredRoles);
  };

  const hasPermission = (permission) => {
    if (!state.user || !state.user.org_memberships) return false;
    
    // Check if user has ULTRA_ADMIN role (which has all permissions)
    const hasUltraAdmin = state.user.org_memberships.some(membership => 
      membership.role === 'ULTRA_ADMIN' && membership.permissions.includes('*')
    );
    
    if (hasUltraAdmin) return true;
    
    // Check specific permissions across all memberships
    return state.user.org_memberships.some(membership =>
      membership.permissions.includes(permission)
    );
  };

  const value = {
    ...state,
    login,
    logout,
    refreshToken: refreshTokenFunc,
    impersonateUser,
    stopImpersonation,
    hasRole,
    hasPermission,
    auditLog
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};